import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

function mapsKey() {
  if (typeof document === "undefined" || !document.documentElement || !document.documentElement.dataset) {
    return "";
  }
  return document.documentElement.dataset.mapsKey || "";
}

function str(v) {
  if (typeof v === "string") {
    return v.trim();
  }
  if (typeof v === "number" && isFinite(v)) {
    return String(v);
  }
  return "";
}

function isLat(v) {
  return typeof v === "number" && isFinite(v) && v >= -90 && v <= 90;
}

function isLng(v) {
  return typeof v === "number" && isFinite(v) && v >= -180 && v <= 180;
}

function cancelError() {
  const err = new Error("geocoder request cancelled");
  err.cancelled = true;
  return err;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      const request = this.axiosRequest;
      this.axiosRequest = null;
      request.source.cancel();
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.label && !g.street && !g.city) {
      p.push(g.label);
    }

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _type(p) {
    const type = str(p.type).toLowerCase();
    if (type) {
      return type;
    }

    // Legacy-Fallback für Antworten ohne properties.type
    const key = str(p.osm_key);
    const value = str(p.osm_value);
    if (key === "highway") {
      return "street";
    }
    if (key === "place" && (value === "city" || value === "town" || value === "village")) {
      return "city";
    }
    if (key === "boundary" || (key === "place" && value === "suburb")) {
      return "district";
    }
    if (str(p.housenumber)) {
      return "house";
    }
    return "";
  }

  _geoaddress(l) {
    if (!l || typeof l !== "object" || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    const lng = l.geometry.coordinates[0];
    const lat = l.geometry.coordinates[1];
    if (!isLat(lat) || !isLng(lng)) {
      return null;
    }

    const p = l.properties && typeof l.properties === "object" ? l.properties : {};
    const type = this._type(p);
    const name = str(p.name);

    const geoaddress = {};
    let street = str(p.street);
    let city = str(p.city);
    let district = str(p.district);
    let label = "";

    switch (type) {
      case "house":
        if (!street) {
          label = name;
        }
        break;
      case "street":
        if (!street) {
          street = name;
        }
        break;
      case "city":
        if (!city) {
          city = name;
        } else if (name && name !== city && !district) {
          district = name;
        }
        break;
      case "district":
        if (name && name !== city) {
          district = name;
        }
        if (!city && !district) {
          label = name;
        }
        break;
      default:
        if (!street && !city) {
          label = name;
        }
    }

    if (street) {
      geoaddress["street"] = street;
      const number = str(p.housenumber);
      if (number) {
        geoaddress["number"] = number;
      }
    }

    const zip = str(p.postcode);
    if (zip) {
      geoaddress["zip"] = zip;
    }
    if (city) {
      geoaddress["city"] = city;
    }
    if (district && district !== city) {
      geoaddress["district"] = district;
    }
    if (label) {
      geoaddress["label"] = label;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = str(p.state) || undefined;
    geoaddress["country"] = str(p.country) || undefined;

    geoaddress["text"] = this._geoaddressText(geoaddress);

    return geoaddress;
  }

  _features(data) {
    return data && Array.isArray(data.features) ? data.features : [];
  }

  autocomplete(q) {
    this.cancelAutocomplete();

    const query = str(q);
    const key = mapsKey();
    if (!query || !key) {
      return Promise.resolve([]);
    }

    const CancelToken = this.axios.CancelToken;
    const request = {
      source: CancelToken.source()
    };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("key", key);

    return new Promise((resolve, reject) => {
      this.axios.get(SEARCH_URL, {
        cancelToken: request.source.token,
        timeout: REQUEST_TIMEOUT,
        params
      }).then(res => {
        const results = [];

        this._features(res.data).forEach(l => {
          const geoaddress = this._geoaddress(l);
          if (geoaddress) {
            results.push(geoaddress);
          }
        });

        resolve(results);
      }).catch(thrown => {
        if (this.axios.isCancel(thrown)) {
          reject(cancelError());
          return;
        }
        reject(thrown);
      }).finally(() => {
        if (this.axiosRequest === request) {
          this.axiosRequest = null;
        }
      });
    });
  }

  reverse(lat, lng) {
    const nlat = Number(lat);
    const nlng = Number(lng);
    if (!isLat(nlat) || !isLng(nlng)) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    const key = mapsKey();
    if (!key) {
      return Promise.reject(new Error("maps key missing"));
    }

    const params = new URLSearchParams();
    params.append("lat", String(nlat));
    params.append("lon", String(nlng));
    params.append("lang", "de");
    params.append("key", key);

    return this.axios.get(REVERSE_URL, {
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      const features = this._features(res.data);
      for (let i = 0; i < features.length; i++) {
        const geoaddress = this._geoaddress(features[i]);
        if (geoaddress) {
          return geoaddress;
        }
      }
      throw new Error("invalid data");
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
